/**
 * IP Intel Report for IIT Bombay faculty, co-funded by IRCC — the
 * "register interest" page at /ip-intel-report/iit-bombay (alias
 * /ip-intel-report/ircc).
 *
 * Built from the owner's prototype (2026-10-12) on the IP Intel page's own
 * chrome and styles (IpIntelShell, the .ipr scope), with the prototype's
 * slips fixed: the nav has separate "What's inside" and "Case study" links,
 * "Explore a success story" is a real link, no fixed widths, the external
 * link opens in a new tab, and the date, the contact e-mail and the prices
 * come from the server.
 *
 * Faculty register 1-N technologies (title + TRL); nothing is paid on the
 * site. Settings and live state (open, closing date, places left, share,
 * IRCC e-mail) come from GET /api/v1/ip-intel/programme/iitb_ircc/config,
 * editable in Admin → IP Intel Report → IRCC registrations. With no closing
 * date the page says registrations close once the cap is reached. With
 * places unlimited (places_limited false) there is no places count and no
 * cap wording anywhere: only "No payment now." and the closing date, if set.
 *
 * Not for search engines: the page sets robots noindex while mounted.
 * Tracking: every event is ipintel_* (ipintel_ircc_* for this page's own
 * actions) and carries {surface: 'ircc', programme: 'iitb_ircc'}.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePageTitle } from '../../utils/pageTitle';
import { IpIntelShell } from './ipintel/Shell';
import { SamplesSection } from './ipintel/SamplesSection';
import { RegisterInterest } from './ipintel/RegisterInterest';
import { ipIntelApi, IpIntelSample, SampleDelivery } from './ipintel/api';
import { irccApi, IrccProgramme } from './ipintel/irccApi';
import { currentIpIntelPage, ipClip, ipTrack, setIpTrackContext, useScrollDepth, useSectionViews } from './ipintel/track';
import { usePageMeta } from './ipintel/seo';
import { FALLBACK_SAMPLES } from './ipintel/content';
import { withUsd } from './ipintel/usd';
import { SubmarkLogo } from './Icons';
import {
  IRCC_ABOUT, IRCC_CANONICAL, IRCC_CASE_STUDY, IRCC_FALLBACK, IRCC_KEY, IRCC_META, IRCC_MODULES,
  closesOn, irccFaqs, irccSteps, placesLimited,
} from './ipintel/irccContent';

type ConfigState = 'loading' | 'ready' | 'error';
type RegisterPosition = 'nav' | 'hero' | 'scope_card';

const TRACK = { surface: 'ircc', programme: IRCC_KEY };

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/** robots noindex while the page is mounted; the tag is put back as it was. */
const useNoIndex = () => {
  useEffect(() => {
    let el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const created = !el;
    const old = el ? el.getAttribute('content') : null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, nofollow');
    const tag = el;
    return () => {
      try {
        if (created) tag.remove();
        else if (old !== null) tag.setAttribute('content', old);
        else tag.removeAttribute('content');
      } catch { /* ignore */ }
    };
  }, []);
};

const CSS = `
/* Side padding outside the 1200px column, as .ipr-section has it, so the
   header's edges line up with the content below at every width. */
.ipr .ircc-header { position: sticky; top: 0; z-index: 40; background: rgba(255, 255, 255, 0.92); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border-bottom: 1px solid var(--ipr-border); padding: 0 40px; }
.ipr .ircc-header-inner { display: flex; align-items: center; justify-content: space-between; gap: 12px 28px; min-height: 76px; padding: 10px 0; }
.ipr .ircc-brand { display: flex; align-items: center; gap: 10px; min-width: 0; text-decoration: none; color: var(--ipr-heading); }
.ipr .ircc-brand:focus-visible { outline: 2px solid var(--ipr-primary-500); outline-offset: 4px; border-radius: 4px; }
/* SubmarkLogo's box has ~9px of air left of the shield; pull it onto the text line. */
.ipr .ircc-brand-mark { margin: -4px -4px -4px -10px; }
.ipr .ircc-brand-ut { font-family: var(--ipr-font-display); font-size: 16px; font-weight: 700; letter-spacing: -0.01em; white-space: nowrap; }
.ipr .ircc-header-right { display: flex; align-items: center; gap: 28px; }
.ipr .ircc-subnav { display: flex; flex-wrap: wrap; gap: 6px 24px; font-family: var(--ipr-font-ui); font-size: 14px; font-weight: 600; }
.ipr .ircc-subnav a { color: var(--ipr-heading); text-decoration: none; padding: 6px 0; border-radius: 2px; }
.ipr .ircc-subnav a:hover { color: var(--ipr-primary-600); }
.ipr .ircc-subnav a:focus-visible, .ipr .ircc-link:focus-visible { outline: 2px solid var(--ipr-primary-500); outline-offset: 2px; }
.ipr .ircc-smallprint { margin: 22px 0 0; font-size: 14px; line-height: 1.6; color: var(--ipr-gray-300); }
.ipr .ircc-smallprint strong { color: var(--ipr-heading); }
.ipr .ircc-about { background: var(--ipr-gray-50); border-top: 1px solid var(--ipr-border); border-bottom: 1px solid var(--ipr-border); padding: 28px 40px; }
.ipr .ircc-about-inner { display: flex; flex-wrap: wrap; gap: 10px 32px; align-items: baseline; }
.ipr .ircc-about p { margin: 0; flex: 1 1 520px; min-width: 0; font-size: 16px; line-height: 1.6; color: var(--ipr-text); }
.ipr .ircc-case { background: #13233F; color: #fff; border-radius: 18px; padding: 48px; display: flex; flex-wrap: wrap; gap: 32px 56px; align-items: center; }
.ipr .ircc-case-main { flex: 999 1 480px; min-width: 0; display: flex; flex-direction: column; gap: 16px; }
.ipr .ircc-case-main h2 { font-family: var(--ipr-font-display); font-size: 30px; line-height: 1.2; font-weight: 700; margin: 0; color: #fff; }
.ipr .ircc-case-main p { margin: 0; font-size: 16px; line-height: 1.6; color: #D6DCE6; }
.ipr .ircc-case-main a { color: #F1E3C8; font-weight: 600; font-size: 15px; text-decoration: underline; align-self: flex-start; }
.ipr .ircc-case-main a:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.ipr .ircc-case-facts { flex: 1 1 260px; min-width: 0; display: flex; flex-direction: column; gap: 14px; border-left: 1px solid #2E3F5E; padding-left: 32px; margin: 0; }
.ipr .ircc-case-facts dt { font-size: 13px; color: #A9B4C6; }
.ipr .ircc-case-facts dd { margin: 2px 0 0; font-size: 16px; font-weight: 600; color: #fff; }
.ipr .ircc-register { display: grid; grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.4fr); gap: 48px; align-items: start; }
.ipr .ircc-card { border: 1px solid var(--ipr-border); border-radius: 16px; padding: 36px; background: #fff; box-shadow: var(--ipr-shadow-card); display: flex; flex-direction: column; gap: 18px; min-width: 0; scroll-margin-top: 104px; }
.ipr .ircc-form { display: flex; flex-direction: column; gap: 22px; }
.ipr .ircc-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.ipr .ircc-input { height: 46px; font-size: 15px; }
.ipr .ircc-stepper { display: inline-flex; align-items: center; border: 1px solid var(--ipr-border); border-radius: 8px; height: 46px; overflow: hidden; background: #fff; }
.ipr .ircc-stepper button { width: 46px; height: 100%; border: 0; background: var(--ipr-gray-50); font-size: 20px; line-height: 1; color: var(--ipr-heading); cursor: pointer; font-family: var(--ipr-font-display); }
.ipr .ircc-stepper button:hover:not(:disabled) { background: var(--ipr-primary-100); }
.ipr .ircc-stepper button:disabled { color: var(--ipr-gray-200); cursor: not-allowed; }
.ipr .ircc-stepper button:focus-visible { outline: 2px solid var(--ipr-primary-500); outline-offset: -2px; }
.ipr .ircc-stepper span { min-width: 56px; text-align: center; font-family: var(--ipr-font-display); font-size: 16px; font-weight: 700; color: var(--ipr-heading); }
.ipr .ircc-techs { display: flex; flex-direction: column; gap: 14px; }
.ipr .ircc-tech { margin: 0; min-width: 0; border: 1px solid var(--ipr-border); border-radius: 12px; padding: 14px 18px 18px; background: #FAF9F7; display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 14px; }
.ipr .ircc-tech legend { font-family: var(--ipr-font-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--ipr-primary-600); padding: 0 6px; }
.ipr .ircc-consent { display: flex; gap: 12px; align-items: flex-start; font-size: 14px; line-height: 1.55; color: var(--ipr-text); cursor: pointer; }
.ipr .ircc-consent input { width: 18px; height: 18px; margin: 2px 0 0; accent-color: #886A44; flex-shrink: 0; cursor: pointer; }
.ipr .ircc-consent strong { color: var(--ipr-heading); }
.ipr .ircc-submit-row { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
.ipr .ircc-hint { font-size: 13px; color: var(--ipr-gray-300); }
.ipr .ircc-check { width: 48px; height: 48px; border-radius: 999px; background: var(--ipr-primary-100); display: flex; align-items: center; justify-content: center; }
.ipr .ircc-h3 { font-family: var(--ipr-font-display); font-size: 24px; line-height: 1.25; font-weight: 700; color: var(--ipr-heading); margin: 0; }
.ipr .ircc-p { font-size: 15px; line-height: 1.6; color: var(--ipr-text); margin: 0; }
.ipr .ircc-next { margin: 0; padding-left: 20px; font-size: 15px; line-height: 1.7; color: var(--ipr-text); list-style: decimal; }
.ipr .ircc-link { color: var(--ipr-primary-600); font-weight: 600; text-decoration: underline; word-break: break-word; }
.ipr .ircc-cofoot { border-top: 1px solid var(--ipr-border); padding: 24px 40px; }
.ipr .ircc-cofoot-inner { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px 24px; font-size: 13px; line-height: 1.6; color: var(--ipr-gray-300); }
.ipr .ircc-cofoot strong { color: var(--ipr-heading); }
@media (max-width: 1060px) {
  .ipr .ircc-subnav { display: none; }
}
@media (max-width: 900px) {
  .ipr .ircc-header, .ipr .ircc-about, .ipr .ircc-cofoot { padding-left: 24px; padding-right: 24px; }
  .ipr .ircc-register { grid-template-columns: minmax(0, 1fr); gap: 32px; }
}
@media (max-width: 600px) {
  .ipr .ircc-header-inner { min-height: 64px; }
  .ipr .ircc-brand { gap: 6px; }
  .ipr .ircc-case { padding: 32px 22px; border-radius: 14px; }
  .ipr .ircc-case-main h2 { font-size: 24px; }
  .ipr .ircc-case-facts { border-left: 0; padding-left: 0; border-top: 1px solid #2E3F5E; padding-top: 20px; }
  .ipr .ircc-card { padding: 24px 18px; }
  .ipr .ircc-grid-2, .ipr .ircc-tech { grid-template-columns: minmax(0, 1fr); }
}
`;

const IpIntelIrccPage: React.FC = () => {
  // Every ipintel_* event on this page says where it came from. Set during
  // render as well: children's effects run before this component's own.
  setIpTrackContext(TRACK);
  useEffect(() => {
    setIpTrackContext(TRACK);
    return () => setIpTrackContext(null);
  }, []);

  usePageTitle('IP Intelligence Report for IIT Bombay faculty');
  usePageMeta({ title: IRCC_META.title, description: IRCC_META.description, canonical: IRCC_CANONICAL, type: 'website' });
  useNoIndex();

  const [programme, setProgramme] = useState<IrccProgramme | null>(null);
  const [configState, setConfigState] = useState<ConfigState>('loading');
  const [samples, setSamples] = useState<IpIntelSample[] | null>(null);
  const [sampleDelivery, setSampleDelivery] = useState<SampleDelivery | undefined>(undefined);
  const viewTracked = useRef(false);

  const loadProgramme = useCallback(async (attempt = 0): Promise<IrccProgramme | null> => {
    setConfigState('loading');
    const res = await irccApi.getProgramme();
    if (res.ok) {
      setProgramme(res.data);
      setConfigState('ready');
      return res.data;
    }
    // One quiet retry for a flaky network before showing the error state.
    if (attempt === 0 && res.status !== 404) {
      await new Promise(r => setTimeout(r, 2500));
      return loadProgramme(1);
    }
    setConfigState('error');
    return null;
  }, []);

  /** Re-read the live state (places left, open) without disturbing the form. */
  const refreshProgramme = useCallback(async () => {
    const res = await irccApi.getProgramme();
    if (res.ok) { setProgramme(res.data); setConfigState('ready'); }
  }, []);

  useEffect(() => {
    void loadProgramme().then(p => {
      if (viewTracked.current) return;
      viewTracked.current = true;
      ipTrack('ipintel_ircc_page_view', {
        config: p ? 'ok' : 'error', open: p ? p.open : null,
        remaining: p ? p.remaining : null, closed_reason: p?.closed_reason || null,
      });
    });
    // The sample cards (and how they are delivered) come from the main IP Intel config.
    void ipIntelApi.getConfig().then(res => {
      if (res.ok && Array.isArray(res.data?.samples)) {
        setSamples(res.data.samples);
        if (res.data.sample_delivery) setSampleDelivery(res.data.sample_delivery);
      } else {
        setSamples(FALLBACK_SAMPLES);
      }
    });
  }, [loadProgramme]);

  useSectionViews(true);
  useScrollDepth(currentIpIntelPage(), true);

  const ready = configState === 'ready' && !!programme;
  const share = programme?.share_label || IRCC_FALLBACK.share_label;
  const shareAmount = programme?.share_amount_label || IRCC_FALLBACK.share_amount_label;
  const taxLabel = programme ? programme.tax_label : IRCC_FALLBACK.tax_label;
  const fullPrice = programme?.full_price_label || IRCC_FALLBACK.full_price_label;
  const contact = programme?.contact_email || IRCC_FALLBACK.contact_email;
  // The cap wording only once the config says places are limited (never
  // while it loads, so an unlimited programme never flashes a cap).
  const limited = ready && placesLimited(programme);
  const cap = programme?.tech_cap || IRCC_FALLBACK.tech_cap;
  const days = programme?.turnaround_days || IRCC_FALLBACK.turnaround_days;
  const closesLabel = closesOn(programme?.closes_at);
  const steps = irccSteps(share, days);
  const faqs = irccFaqs(share, days);

  const goRegister = (position: RegisterPosition) => (e?: React.MouseEvent) => {
    e?.preventDefault();
    ipTrack('ipintel_ircc_click_register', { position });
    scrollToId('register');
  };

  const anchor = (id: string, item: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (id === 'case-study') ipTrack('ipintel_ircc_click_case_study', { position: 'nav' });
    else ipTrack('ipintel_nav_click', { item, page: 'ircc' });
    scrollToId(id);
  };

  const skel = (w: number, h: number) => <span className="ipr-skel" style={{ width: w, height: h }} aria-label="Loading" />;

  /** The line under the hero buttons: date (or the cap rule), places left, or
   *  closed. Unlimited places: "No payment now." and the date, if one is set. */
  const heroSmallPrint = () => {
    if (!ready) return configState === 'loading' ? <>No payment now. {skel(180, 12)}</> : <>No payment now.</>;
    const p = programme!;
    if (!p.open) {
      return limited && p.closed_reason === 'full'
        ? <>All {p.tech_cap} places are taken, so registrations have closed.</>
        : <><strong>Registrations have closed.</strong>{p.closed_reason === 'deadline' && closesLabel ? ` They closed on ${closesLabel}.` : ''}</>;
    }
    if (!limited) {
      return closesLabel ? <>No payment now. Registrations close on {closesLabel}.</> : <>No payment now.</>;
    }
    const left = <> · <strong>{p.remaining} of {p.tech_cap}</strong> places left</>;
    return closesLabel
      ? <>No payment now. Registrations close on {closesLabel}{left}.</>
      : <>No payment now. Registrations close once {p.tech_cap} technologies are in{left}.</>;
  };

  const howNote = !limited
    ? (ready && closesLabel
      ? `Nothing is charged when you register. Registrations close on ${closesLabel}.`
      : 'Nothing is charged when you register.')
    : closesLabel
      ? `Nothing is charged when you register. Registrations close on ${closesLabel}, or earlier once ${cap} technologies are in.`
      : `Nothing is charged when you register. Registrations close once ${cap} technologies are in.`;

  // One header, the prototype's: our logo (drawn in code; no partner logo —
  // owner, 2026-10-27), the page's sections and the CTA. It takes the place
  // of the site's NavBar, so there is no second bar.
  const header = (
    <header className="ircc-header">
      <div className="ipr-wrap ircc-header-inner">
        <a className="ircc-brand" href="/" aria-label="UniversityTech.io: go to the homepage"
          onClick={() => ipTrack('ipintel_nav_click', { item: 'logo', page: currentIpIntelPage() })}>
          <span className="ircc-brand-mark" aria-hidden="true"><SubmarkLogo /></span>
          <span className="ircc-brand-ut" aria-hidden="true">UniversityTech.io</span>
        </a>
        <div className="ircc-header-right">
          <nav aria-label="Page sections" className="ircc-subnav">
            <a href="#scope" onClick={anchor('scope', 'whats_inside')}>What's inside</a>
            <a href="#case-study" onClick={anchor('case-study', 'case_study')}>Case study</a>
            <a href="#how" onClick={anchor('how', 'how_it_works')}>How it works</a>
            <a href="#samples" onClick={anchor('samples', 'sample_reports')}>Sample reports</a>
          </nav>
          <a className="ipr-btn-primary ipr-nav-order" href="#register" onClick={goRegister('nav')}>Register interest</a>
        </div>
      </div>
    </header>
  );

  return (
    <IpIntelShell
      onSelf={() => { window.location.href = '/ip-intel-report'; }}
      header={header}
    >
      <style>{CSS}</style>

      {/* ============================== HERO ============================== */}
      <section className="ipr-section ipr-sec-hero">
        <div className="ipr-wrap ipr-hero">
          <div>
            <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 18 }}>
              For IIT Bombay faculty · 50% funded by IRCC
            </div>
            <h1 className="ipr-h1">What is your IP worth?</h1>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--ipr-text)', maxWidth: 600, margin: '0 0 32px' }}>
              An in-depth commercial assessment of your IP: who could license it, what it is worth, and what to do in
              the next 90 days. IRCC is covering half the cost for IIT Bombay faculty.
            </p>
            <div className="ipr-cta-row" style={{ flexWrap: 'wrap' }}>
              <a className="ipr-btn-primary" href="#register" onClick={goRegister('hero')}>Register your interest</a>
              <a className="ipr-btn-secondary" href="#samples"
                onClick={e => { e.preventDefault(); ipTrack('ipintel_click_see_samples', { position: 'hero' }); scrollToId('samples'); }}>
                See sample reports
              </a>
            </div>
            <p className="ircc-smallprint" aria-live="polite">{heroSmallPrint()}</p>
            <a className="ipr-link-underline" href="#case-study" style={{ marginTop: 22 }}
              onClick={e => { e.preventDefault(); ipTrack('ipintel_ircc_click_case_study', { position: 'hero' }); scrollToId('case-study'); }}>
              Explore a success story
            </a>
          </div>

          <aside className="ipr-card ipr-hero-aside" aria-label="At a glance" style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)' }}>At a glance</div>
            <div>
              <div style={{ fontSize: 13, color: 'var(--ipr-muted)', marginBottom: 6 }}>Your share, per technology</div>
              {configState === 'loading' && !programme ? (
                <div style={{ height: 38, display: 'flex', alignItems: 'center' }}>{skel(170, 30)}</div>
              ) : (
                <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 32, fontWeight: 700, color: 'var(--ipr-heading)' }}>
                  {shareAmount}{' '}
                  {taxLabel && <span style={{ fontSize: 15, fontWeight: 400, color: 'var(--ipr-text)' }}>+ {taxLabel}</span>}
                </div>
              )}
              <div style={{ fontSize: 13, color: 'var(--ipr-gray-300)', marginTop: 6, lineHeight: 1.5 }}>
                {/* The USD figure is display only (Admin → IP Intel Report → Pricing). */}
                Full price {withUsd(fullPrice, programme)}. IRCC pays the other half.
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--ipr-muted)', marginBottom: 6 }}>Turnaround</div>
              <div style={{ fontSize: 15, color: 'var(--ipr-heading)', fontWeight: 500 }}>{days} days from when you confirm your technology details</div>
            </div>
            <div style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--ipr-muted)', marginBottom: 6 }}>To register</div>
              <div style={{ fontSize: 15, color: 'var(--ipr-heading)', fontWeight: 500 }}>Your details, plus the title and TRL of each technology</div>
            </div>
          </aside>
        </div>
      </section>

      {/* ========================== ABOUT STRIP ========================== */}
      <section data-ipintel-section="about" className="ircc-about">
        <div className="ipr-wrap ircc-about-inner">
          <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)' }}>About UniversityTech.io</div>
          <p>{IRCC_ABOUT}</p>
        </div>
      </section>

      {/* ============================= SCOPE ============================= */}
      <section id="scope" data-ipintel-section="scope" className="ipr-section ipr-sec-scope" style={{ background: 'var(--ipr-gray-50)' }}>
        <div className="ipr-wrap">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, marginBottom: 48, flexWrap: 'wrap' }}>
            <div>
              <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 12 }}>What the report covers</div>
              <h2 className="ipr-h2" style={{ maxWidth: 600 }}>Seven questions the report answers</h2>
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.55, color: 'var(--ipr-text)', maxWidth: 380, margin: 0 }}>
              One module per question. Each stands on its own evidence and feeds the recommendation at the end.
            </p>
          </div>
          <div className="ipr-q-grid">
            {IRCC_MODULES.map(q => (
              <div key={q.n} className="ipr-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 36 }}>
                <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-500)' }}>{q.n}</div>
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-between', gap: 16 }}>
                  <p style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--ipr-heading)', lineHeight: 1.35, margin: 0 }}>{q.q}</p>
                  <div style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 12, fontSize: 13, color: 'var(--ipr-muted)', fontWeight: 600 }}>{q.module}</div>
                </div>
              </div>
            ))}
            <div style={{
              background: 'var(--ipr-primary-100)', border: '1px solid var(--ipr-primary-300)', borderRadius: 12,
              padding: 24, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 20,
            }}>
              <p style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 16, fontWeight: 500, color: 'var(--ipr-primary-700)', lineHeight: 1.5, margin: 0 }}>
                All seven modules, one technology, one document.
              </p>
              <a className="ipr-btn-primary" href="#register" style={{ width: 'fit-content' }} onClick={goRegister('scope_card')}>Register interest</a>
            </div>
          </div>
        </div>
      </section>

      {/* ========================== CASE STUDY ========================== */}
      <section id="case-study" data-ipintel-section="case_study" className="ipr-section">
        <div className="ipr-wrap ircc-case">
          <div className="ircc-case-main">
            <div className="ipr-eyebrow" style={{ color: '#C9B48F' }}>{IRCC_CASE_STUDY.eyebrow}</div>
            <h2>{IRCC_CASE_STUDY.title}</h2>
            <p>{IRCC_CASE_STUDY.body}</p>
            <a href={IRCC_CASE_STUDY.link} target="_blank" rel="noopener noreferrer"
              onClick={() => {
                let host = '';
                try { host = new URL(IRCC_CASE_STUDY.link).host; } catch { /* ignore */ }
                ipTrack('ipintel_click_outbound', { host: ipClip(host, 80), where: 'case_study', page: 'ircc' });
              }}>
              {IRCC_CASE_STUDY.linkLabel}<span className="ipr-hp">(opens in a new tab)</span>
            </a>
          </div>
          <dl className="ircc-case-facts">
            {IRCC_CASE_STUDY.facts.map(f => (
              <div key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ========================= HOW IT WORKS ========================= */}
      <section id="how" data-ipintel-section="how" className="ipr-section" style={{ paddingTop: 0 }}>
        <div className="ipr-wrap">
          <div className="ipr-how-head">
            <div>
              <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 12 }}>How it works</div>
              <h2 className="ipr-h2">Register interest today, pay later</h2>
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-text)', margin: 0, alignSelf: 'center' }}>{howNote}</p>
          </div>
          <ol className="ipr-how-steps" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {steps.map((s, i) => (
              <li key={s.n}>
                <div style={{ height: 3, width: 40, background: i === 0 ? 'var(--ipr-primary-500)' : 'var(--ipr-gray-100)', marginBottom: 16 }} />
                <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)', marginBottom: 8 }}>Step {s.n}</div>
                <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: 8 }}>{s.t}</div>
                <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ipr-text)', margin: 0 }}>{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ======================== SAMPLE REPORTS ======================== */}
      <SamplesSection
        loading={samples === null}
        samples={samples || []}
        sampleDelivery={sampleDelivery}
        showInstitute
        defaultOrganisation="IIT Bombay"
        surface="ircc"
      />

      {/* =========================== REGISTER =========================== */}
      <section id="register" data-ipintel-section="register" className="ipr-section">
        <div className="ipr-wrap ircc-register">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)' }}>Register interest</div>
            <h2 className="ipr-h2">Tell us which technologies you'd like assessed</h2>
            <p className="ircc-p">
              This registers your interest only. Nothing is charged now. IRCC will get in touch with you with next
              steps.
            </p>
            <p className="ircc-p" style={{ fontSize: 14, color: 'var(--ipr-gray-300)' }}>
              Questions? Write to <a className="ircc-link" href={`mailto:${contact}`}>{contact}</a>
            </p>
          </div>
          <RegisterInterest programme={programme} configState={configState}
            onRetry={() => { ipTrack('ipintel_config_retry', { position: 'register' }); void loadProgramme(); }}
            onRefresh={() => { void refreshProgramme(); }} />
        </div>
      </section>

      {/* ============================== FAQ ============================== */}
      <section id="faq" data-ipintel-section="faq" className="ipr-section" style={{ background: 'var(--ipr-gray-50)' }}>
        <div className="ipr-wrap ipr-faq-grid">
          <h2 className="ipr-h2">Before you register</h2>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {faqs.map((f, i) => (
              <div key={f.q} style={{ borderBottom: i < faqs.length - 1 ? '1px solid var(--ipr-border)' : 'none', padding: '20px 0' }}>
                <h3 style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--ipr-heading)', margin: '0 0 8px' }}>{f.q}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ipr-text)', margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================= CO-BRANDED FOOTER ======================= */}
      <div className="ircc-cofoot">
        <div className="ipr-wrap ircc-cofoot-inner">
          <span><strong>UniversityTech.io × IRCC, IIT Bombay</strong> · IP Intelligence Report programme for faculty</span>
          <span>Contact: <a className="ircc-link" href={`mailto:${contact}`}>{contact}</a></span>
        </div>
      </div>
    </IpIntelShell>
  );
};

export default IpIntelIrccPage;
